import * as THREE from "three";
import {
  CONTACT_EMISSIVE_DECAY_TAU,
  CONTACT_EMISSIVE_PEAK,
  CONTACT_IMPULSE_DECAY_TAU,
  CONTACT_IMPULSE_PEAK,
  IMPACT_MAX,
  IMPACT_REF,
  MAX_EMITTERS,
  MAX_OBJECTS,
} from "./constants";
import { performanceCycleSec, phaseAt } from "./clock";
import { Emitter, sequencerNums } from "./emitters";
import { knobOutput, PlacedObject, TimerMode } from "./objects";
import { PulsePayload, ValueNode } from "./signals";
import { AppContext, NumericParam } from "./appContext";

interface SignalRuntimeDeps {
  settableParams(id: string): NumericParam[];
  emitterById(id: string): Emitter | null;
  expandParamTargets(id: string): string[];
  bindPitchValue(targetId: string, value: number): number;
  paramBase: Map<string, number>;
  markParamPanelDirty(): void;
  resolveCountDisplay(id: string, value: number, dt: number): number;
}

export interface SignalRuntime {
  injExternal: number[];
  injDriven: boolean[];
  injSpeed: Array<number | null>;
  clearEmitInjection(): void;
  driveContactGlow(dt: number, counts: Float32Array, impacts: Float32Array): void;
  buildValueNodes(): Map<string, ValueNode>;
  evaluateSignals(packed: readonly PlacedObject[], counts: Float32Array, impacts: Float32Array, dt: number): boolean;
  forceParamReapply(): void;
}

/**
 * Per-frame signal-graph glue: contact-glow decay on collider materials, the
 * value-node snapshot for the resolver, Param-in overwrites (non-destructive via
 * paramBase), and the emit-injection staging arrays the particle system reads.
 */
export function initSignalRuntime(ctx: AppContext, deps: SignalRuntimeDeps): SignalRuntime {
  const { particles, field, emitterField, graph, evaluator, widgets } = ctx;
  const { settableParams, emitterById, expandParamTargets, bindPitchValue, paramBase, markParamPanelDirty, resolveCountDisplay } = deps;

  const injExternal = new Array<number>(MAX_EMITTERS).fill(0);
  const injDriven = new Array<boolean>(MAX_EMITTERS).fill(false);
  const injSpeed = new Array<number | null>(MAX_EMITTERS).fill(null);

  const objectGlow = new Float32Array(MAX_OBJECTS);
  const objectImpulse = new Float32Array(MAX_OBJECTS);

  const lastParamApplied = new Map<string, number>();

  function clearEmitInjection(): void {
    for (let e = 0; e < MAX_EMITTERS; e++) {
      injExternal[e] = 0;
      injSpeed[e] = null;
      injDriven[e] = false;
    }
    const emitters = emitterField.list();
    for (let e = 0; e < emitters.length && e < MAX_EMITTERS; e++) {
      injDriven[e] = graph.edgesTo(emitters[e].id, "emitIn").length > 0;
    }
  }

  function driveContactGlow(dt: number, counts: Float32Array, impacts: Float32Array): void {
    const emissiveDecay = Math.exp(-dt / CONTACT_EMISSIVE_DECAY_TAU);
    const impulseDecay = Math.exp(-dt / CONTACT_IMPULSE_DECAY_TAU);
    const packed = field.packedColliders();
    const count = packed.length;
    for (let i = 0; i < count; i++) {
      if (packed[i].type === "force" || packed[i].type === "rail") {
        continue;
      }
      objectGlow[i] *= emissiveDecay;
      objectImpulse[i] *= impulseDecay;
      const hits = counts[i];
      if (hits > 0) {
        const meanImpact = impacts[i] / hits;
        const strength = Math.min((meanImpact * IMPACT_MAX) / IMPACT_REF, 1);
        objectGlow[i] = Math.min(1, objectGlow[i] + strength);
        objectImpulse[i] = Math.max(objectImpulse[i], strength);
      }
      const mesh = field.packedMeshAt(i);
      if (!mesh) {
        continue;
      }
      const material = mesh.material as THREE.MeshStandardMaterial;
      material.emissive.copy(material.color);
      material.emissiveIntensity = objectGlow[i] * CONTACT_EMISSIVE_PEAK;
      mesh.scale.setScalar(!packed[i].muted && packed[i].volume > 0 ? 1 + objectImpulse[i] * CONTACT_IMPULSE_PEAK : 1);
    }
  }

  /**
   * The value nodes for this frame's value resolver, keyed by id and carrying each
   * node's operator + typed operand (the resolver pulls the live count and the
   * wired inputs itself). Built fresh each frame (≤ MAX_OBJECTS entries).
   */
  function buildValueNodes(): Map<string, ValueNode> {
    const nodes = new Map<string, ValueNode>();
    for (const o of field.list()) {
      if (o.type === "calc") {
        nodes.set(o.id, { operator: o.operator, operand: o.operand, isCount: false });
      } else if (o.type === "count") {
        nodes.set(o.id, { operator: o.operator, operand: o.operand, isCount: true });
      }
    }
    return nodes;
  }

  /**
   * Drives each Param-in edge's chosen target param with its value source's output
   * (any arithmetic happens upstream in the value-node graph; fan-in: each edge
   * writes its own param). The override is non-destructive: the param's pre-drive value is
   * captured in paramBase on the first driven frame and restored once it stops being
   * driven (disconnect / delete). Emitter writes are pushed to the particle system
   * here; collider / force / orbit writes ride the next repack or frame read.
   * A param is only re-set when its resolved value changed since the last frame
   * (lastParamApplied), so a static knob does not re-run heavy setters such as
   * a transpose write that recolors / rebuilds meshes every frame.
   * Returns whether any emitter param changed.
   */
  function applyParamWrites(nodes: Map<string, ValueNode>): boolean {
    let emitterWritten = false;
    const driven = new Set<string>();
    for (const e of graph.all()) {
      if (e.toPort !== "setParamIn" || e.param === "") {
        continue;
      }
      const value = evaluator.valueOut(graph, e.from, nodes);
      for (const targetId of expandParamTargets(e.to)) {
        const entry = settableParams(targetId).find((p: NumericParam): boolean => p.name === e.param);
        if (!entry) {
          continue;
        }
        const key = `${targetId}|${e.param}`;
        if (!paramBase.has(key)) {
          paramBase.set(key, entry.get());
        }
        driven.add(key);
        const applied = e.param === "pitch" ? bindPitchValue(targetId, value) : value;
        if (lastParamApplied.get(key) === applied) {
          continue;
        }
        lastParamApplied.set(key, applied);
        entry.set(applied);
        if (emitterById(targetId)) {
          emitterWritten = true;
        }
      }
    }
    for (const [key, base] of [...paramBase]) {
      if (driven.has(key)) {
        continue;
      }
      const sep = key.indexOf("|");
      const id = key.slice(0, sep);
      const entry = settableParams(id).find((p: NumericParam): boolean => p.name === key.slice(sep + 1));
      if (entry) {
        entry.set(base);
        if (emitterById(id)) {
          emitterWritten = true;
        }
      }
      paramBase.delete(key);
      lastParamApplied.delete(key);
      markParamPanelDirty();
    }
    return emitterWritten;
  }

  /**
   * Runs the signal graph for this frame: each collider's contact count is its
   * pulseOut, button presses since the last frame merge in as the button's
   * pulseOut (impactSum at the IMPACT_REF reference speed, so a driven emitter
   * re-emits at a moderate fixed speed), button hold / knob values feed
   * the value resolver, lamps get their level pushed onto their dome emissive,
   * value nodes accumulate inc pulses, emitIn-driven emitters have their
   * external emit count staged into injExternal/injDriven for the *next* frame's
   * update (the 1-frame delay the brief accepts, since contacts are read after
   * update), and setparam nodes overwrite their target object params. injSpeed is
   * the contacts' mean impact speed in world units (impactSum arrives normalized
   * by IMPACT_MAX), so a driven emitter re-emits at the impacting particles'
   * speed and its own speed sliders are ignored — unless the emitter's
   * pulseSpeedFromSettings toggle is on, which leaves injSpeed null so the
   * emitter's own speedMin/speedMax range applies. The emitter `gate` param
   * (the free-Hz rate multiplier) rides the ordinary Param-in overwrite below —
   * no separate plumbing. The graph runs every frame regardless of the node
   * feature toggle (that toggle only governs node UI / wiring / abstract-node
   * visibility), so a wired scene keeps playing in node-off / Simple play mode.
   */
  function evaluateSignals(packed: readonly PlacedObject[], counts: Float32Array, impacts: Float32Array, dt: number): boolean {
    const pulseByObject = new Map<string, PulsePayload>();
    for (let k = 0; k < packed.length; k++) {
      pulseByObject.set(packed[k].id, { count: counts[k], impactSum: impacts[k] });
    }
    for (const [id, count] of widgets.drainPresses()) {
      pulseByObject.set(id, { count, impactSum: count * (IMPACT_REF / IMPACT_MAX) });
    }
    const countIds: string[] = [];
    const displayIds: string[] = [];
    const randomIds: string[] = [];
    const timerModeById = new Map<string, TimerMode>();
    const decaySById = new Map<string, number>();
    const delaySById = new Map<string, number>();
    const widgetValueById = new Map<string, number>();
    const lampIds: string[] = [];
    const routerIds: string[] = [];
    const seqIds: string[] = [];
    const seqValuesById = new Map<string, readonly number[]>();
    const curveCycleById = new Map<string, number>();
    const routerHoldSById = new Map<string, number>();
    const routerHoldResetById = new Map<string, boolean>();
    const lamps: PlacedObject[] = [];
    const seqNodes: PlacedObject[] = [];
    const globalIds: string[] = [];
    for (const o of field.list()) {
      if (o.type === "global") {
        globalIds.push(o.id);
      }
      if (o.type === "count") {
        countIds.push(o.id);
      }
      if (o.type === "display") {
        displayIds.push(o.id);
      }
      if (o.type === "calc" && o.operator === "random") {
        randomIds.push(o.id);
      }
      if (o.type === "timer") {
        timerModeById.set(o.id, o.timerMode);
      }
      if (o.type === "decay") {
        decaySById.set(o.id, o.decayS);
      }
      if (o.type === "delay") {
        delaySById.set(o.id, o.delayS);
      }
      if (o.type === "button") {
        widgetValueById.set(o.id, (o.buttonMode === "toggle" ? o.buttonOn : widgets.isHeld(o.id)) ? 1 : 0);
      }
      if (o.type === "knob") {
        widgetValueById.set(o.id, knobOutput(o));
      }
      if (o.type === "lamp") {
        lampIds.push(o.id);
        lamps.push(o);
      }
      if (o.type === "router") {
        routerIds.push(o.id);
        routerHoldSById.set(o.id, o.routerHoldS);
        routerHoldResetById.set(o.id, o.routerHoldResetOnPulse);
      }
      if (o.type === "array" || o.type === "curve") {
        seqIds.push(o.id);
        seqValuesById.set(o.id, o.seqValues);
        seqNodes.push(o);
      }
      if (o.type === "curve") {
        curveCycleById.set(o.id, o.seqCycleLength);
      }
    }
    const emitters = emitterField.list();
    const cyclePhase = phaseAt(
      particles.clockTimeSec(),
      performanceCycleSec(sequencerNums(emitters), particles.clockPeriodSeconds()),
      0,
    );
    const result = evaluator.evaluate(graph, pulseByObject, {
      countIds,
      randomIds,
      timerModeById,
      decaySById,
      delaySById,
      widgetValueById,
      lampIds,
      routerIds,
      routerHoldSById,
      routerHoldResetById,
      seqIds,
      seqValuesById,
      curveCycleById,
      globalIds,
      nowSec: particles.clockTimeSec(),
      cyclePhase,
    });
    const nodes = buildValueNodes();
    for (const o of lamps) {
      field.setLampLevel(o.id, Math.max(Math.min(Math.max(o.lampValue, 0), 1), evaluator.lampFlash(o.id)));
    }
    for (const o of seqNodes) {
      field.setSeqCursor(o.id, evaluator.seqDisplayIndex(graph, o.id, nodes, o.seqValues.length));
    }
    for (const id of countIds) {
      field.setCountReadout(id, resolveCountDisplay(id, evaluator.valueOut(graph, id, nodes), dt));
    }
    for (const id of displayIds) {
      field.setDisplayValue(id, evaluator.displayValue(graph, id, nodes));
    }
    clearEmitInjection();
    for (let e = 0; e < emitters.length && e < MAX_EMITTERS; e++) {
      const driven = graph.edgesTo(emitters[e].id, "emitIn").length > 0;
      injDriven[e] = driven;
      const payload = driven ? result.emitInByEmitter.get(emitters[e].id) : undefined;
      injExternal[e] = payload?.count ?? 0;
      if (payload && payload.count > 0 && !emitters[e].pulseSpeedFromSettings) {
        injSpeed[e] = (payload.impactSum / payload.count) * IMPACT_MAX;
      }
    }
    if (applyParamWrites(nodes)) {
      particles.setEmitters(emitterField.liveList());
    }
    const targetOrbits = new Set<string>();
    for (const o of field.list()) {
      if (o.type !== "orbit") {
        continue;
      }
      const edges = graph.edgesTo(o.id, "target");
      if (edges.length === 0) {
        continue;
      }
      targetOrbits.add(o.id);
      evaluator.advanceOrbitProgress(o.id, evaluator.valueOut(graph, edges[0].from, nodes), o.orbit.periodPct.x);
    }
    evaluator.pruneOrbitProgress(targetOrbits);
    return result.globalResetPulsed;
  }

  /**
   * Drops the per-param applied-value cache so the next evaluateSignals re-writes
   * every driven param. Needed after a param's value is changed outside the signal
   * graph (e.g. a remote op resets a node-driven param to its serialized base):
   * the cache would otherwise treat the unchanged source value as "already applied"
   * and never re-drive it, leaving the param stuck at the base.
   */
  function forceParamReapply(): void {
    lastParamApplied.clear();
  }

  return { injExternal, injDriven, injSpeed, clearEmitInjection, driveContactGlow, buildValueNodes, evaluateSignals, forceParamReapply };
}
